import type { DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, theme } from 'antd';
import type { Lead, LeadStatus } from '@entities/crm';
import { LeadCard } from './lead-card';

const { Text } = Typography;

const STATUS_ACCENT: Record<LeadStatus, string> = {
  NEW: '#2563eb',
  IN_TOUCH: '#f59e0b',
  QUOTED: '#8b5cf6',
  WON: '#10b981',
  LOST: '#94a3b8',
};

interface BoardColumnProps {
  status: LeadStatus;
  leads: Lead[];
  hover: boolean;
  draggingId: string | null;
  onDragOver: (status: LeadStatus, e: DragEvent<HTMLDivElement>) => void;
  onDrop: (status: LeadStatus, e: DragEvent<HTMLDivElement>) => void;
  onDragStartCard: (id: string) => void;
  onDragEndCard: () => void;
  onSelect: (id: string) => void;
}

export function BoardColumn({
  status,
  leads,
  hover,
  draggingId,
  onDragOver,
  onDrop,
  onDragStartCard,
  onDragEndCard,
  onSelect,
}: BoardColumnProps) {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const accent = STATUS_ACCENT[status];
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        onDragOver(status, e);
      }}
      onDrop={(e) => {
        e.preventDefault();
        onDrop(status, e);
      }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        minHeight: 420,
        padding: 12,
        borderRadius: 12,
        background: hover ? `${accent}14` : token.colorFillQuaternary,
        border: `1px dashed ${hover ? accent : token.colorBorderSecondary}`,
        transition: 'all 200ms ease',
      }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingBottom: 6,
          borderBottom: `2px solid ${accent}`,
        }}
      >
        <Text style={{ fontWeight: 700, letterSpacing: 0.4, fontSize: 12, textTransform: 'uppercase', color: accent }}>
          {t(`crm.columns.${status}`)}
        </Text>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {leads.length}
        </Text>
      </header>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {leads.map((lead) => (
          <LeadCard
            key={lead.id}
            lead={lead}
            dragging={draggingId === lead.id}
            onDragStart={() => onDragStartCard(lead.id)}
            onDragEnd={onDragEndCard}
            onClick={() => onSelect(lead.id)}
          />
        ))}
      </div>
    </div>
  );
}
